import { supabase } from '../../utils/supabaseClient';
import { logger } from '../../utils/logger';
import type { RPCError } from '../../types/rpc-contracts';

export class RPCException extends Error {
  readonly rpcError: RPCError;

  constructor(rpcError: RPCError) {
    super(rpcError.message);
    this.name = 'RPCException';
    this.rpcError = rpcError;
  }
}

const formatDuration = (startTime: number): number => Number((performance.now() - startTime).toFixed(2));

const isRpcLikeError = (error: unknown): error is { code?: string; message?: string } => {
  return Boolean(error) && typeof error === 'object' && ('message' in (error as Record<string, unknown>) || 'code' in (error as Record<string, unknown>));
};

const wrapError = (functionName: string, error: unknown): RPCException => {
  if (error instanceof RPCException) {
    return error;
  }

  if (isRpcLikeError(error)) {
    return new RPCException({
      code: error.code ?? 'RPC_CALL_FAILED',
      message: error.message ?? `${functionName} failed`,
      details: error,
      timestamp: new Date().toISOString()
    });
  }

  const message = error instanceof Error ? error.message : `${functionName} failed`;
  return new RPCException({
    code: 'RPC_CALL_FAILED',
    message,
    details: error,
    timestamp: new Date().toISOString()
  });
};

export const createRpcError = (
  code: string,
  message: string,
  details?: unknown
): RPCException => {
  return new RPCException({
    code,
    message,
    details,
    timestamp: new Date().toISOString()
  });
};

export interface RpcCallOptions<TInput, TResult> {
  params?: Record<string, unknown> | null;
  transform?: (data: TInput) => TResult;
  successMeta?: (result: TResult, durationMs: number) => Record<string, unknown> | void;
}

export async function callRpc<TInput = unknown, TResult = TInput>(
  functionName: string,
  options: RpcCallOptions<TInput, TResult> = {}
): Promise<TResult> {
  const { params, transform, successMeta } = options;
  const startTime = performance.now();

  try {
    const { data, error } = await supabase.schema('prod').rpc(functionName, params ?? {});

    if (error) {
      throw wrapError(functionName, error);
    }

    const result = transform ? transform(data as TInput) : (data as TResult);
    const durationMs = formatDuration(startTime);
    const meta = successMeta ? successMeta(result, durationMs) : undefined;

    logger.debug(`rpc:${functionName} succeeded`, {
      durationMs,
      ...(meta ?? {})
    });

    return result;
  } catch (rawError) {
    const wrapped = wrapError(functionName, rawError);
    logger.error(`rpc:${functionName} failed`, {
      durationMs: formatDuration(startTime),
      error: wrapped.rpcError
    });
    throw wrapped;
  }
}

export const isRPCError = (error: unknown): error is RPCException => error instanceof RPCException;

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof RPCException) {
    return error.rpcError.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
};
